import type { Outcome } from "@landing/types"
import { motion } from "framer-motion"
import { memo } from "react"
import { OutcomePriceDisplay } from "./components/OutcomePriceDisplay"
import { OutcomeTradeIndicators } from "./components/OutcomeTradeIndicators"
import { useOutcomeInteraction } from "./hooks/use-outcome-interaction"
import { getFullTeamName } from "./utils/market-utils"
import {
  getOutcomeBackgroundColor,
  getOutcomeOpacity,
  isOutcomeClickable,
  isOutcomeEliminated,
} from "./utils/outcome-utils"

interface BracketCardOutcomeRowProps {
  outcome: Outcome
  index: number
  isSelected: boolean
  isExpanded: boolean
  showLogos: boolean
  displayMode: "chance" | "price"
  state: "live" | "upcoming" | "final"
  isFinal: boolean
  heldOutcomes: string[]
  recentTrades: Record<string, { type: "buy" | "sell"; timestamp: number }>
  formatPnL: (amount: number) => string
  getOutcomePnL: (outcome: Outcome) => number
  onOutcomeClick: (outcomeName: string) => void
  selectedOutcomes: string[]
}

function BracketCardOutcomeRowComponent({
  outcome,
  index,
  isSelected,
  showLogos,
  displayMode,
  state,
  isFinal,
  heldOutcomes,
  recentTrades,
  formatPnL,
  getOutcomePnL,
  onOutcomeClick,
  selectedOutcomes,
}: BracketCardOutcomeRowProps) {
  const isClickable = isOutcomeClickable(outcome, heldOutcomes)
  const isEliminated = isOutcomeEliminated(outcome, heldOutcomes)

  const { isPressed, handlers } = useOutcomeInteraction({
    outcome,
    heldOutcomes,
    selectedOutcomes,
    onOutcomeClick,
    isClickable,
  })

  const price = Number.parseFloat(outcome.price)

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{
        opacity: 1,
        x: 0,
        scale: isSelected ? (isPressed ? 1.0 : 1.01) : isPressed ? 0.99 : 1.0,
      }}
      transition={{
        duration: 0.05,
        delay: isSelected ? 0 : index * 0.0125,
        ease: "easeOut",
      }}
      className={`relative h-11 px-3 overflow-hidden transition-all duration-100 touch-manipulation outcome-row group select-none ${
        isClickable ? "cursor-pointer" : "cursor-not-allowed"
      }`}
      {...handlers}
    >
      <div className="absolute inset-0 mx-2">
        <div
          className={`absolute inset-0 rounded-md transform skew-x-[-16deg] ${
            isEliminated ? "bg-white/0" : "bg-white/4"
          }`}
        />

        {state !== "upcoming" && (
          <motion.div
            initial={{ width: isFinal ? `${price}%` : 0 }}
            animate={{ width: `${price}%` }}
            transition={{
              duration: isFinal ? 0 : 0.0625,
              delay: isFinal ? 0 : index * 0.0125 + 0.0375,
              ease: "easeOut",
            }}
            className="absolute inset-y-0 left-0 transform skew-x-[-16deg] origin-left transition-all duration-150"
            style={{
              backgroundColor: getOutcomeBackgroundColor(outcome, heldOutcomes),
              borderRadius: isFinal && price === 100 ? "6px 6px 6px 6px" : "6px 1.5px 1.5px 6px",
              opacity: getOutcomeOpacity(outcome, isSelected, heldOutcomes),
              animation: isFinal ? "none" : "meterSurge 3s ease-in-out infinite",
              animationDelay: `${index * 0.5}s`,
            }}
          />
        )}

        {isSelected && (
          <div
            className={`absolute inset-0 rounded-md border-[1.5px] transform skew-x-[-16deg] ${
              outcome.state === "eliminated" && heldOutcomes.includes(outcome.slug)
                ? "border-[#FF2D30]"
                : "border-[#22FF65]"
            }`}
          />
        )}

        <div
          className={`absolute inset-0 rounded-md transform skew-x-[-16deg] border border-white/5 transition-colors duration-50 ${
            isClickable ? "group-hover:border-white/10" : ""
          }`}
        />
      </div>

      <div className="flex items-center justify-between z-10 relative h-full">
        <div className="flex items-center gap-2.5">
          {outcome.imageSrc && outcome.imageSrc.trim() !== "" && outcome.imageSrc.startsWith("http") && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                delay: index * 0.0125 + 0.025,
              }}
              className={`w-6 h-6 rounded-full flex items-center justify-center ml-2 overflow-hidden ${
                isEliminated ? "opacity-40" : ""
              }`}
              style={{
                backgroundColor: outcome.imageSrc.startsWith("http") ? "transparent" : outcome.imageSrc,
              }}
            >
              {showLogos && (
                <motion.img
                  src={outcome.imageSrc}
                  alt={outcome.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />
              )}
              {!showLogos && <div className="w-full h-full bg-white/0 backdrop-blur-none" />}
            </motion.div>
          )}
          <div className="flex items-center gap-1.5">
            <span
              className={`max-w-[165px] lg:max-w-none truncate block ${
                isEliminated ? "text-white/50" : ""
              } ${!outcome.imageSrc?.startsWith("http") ? "ml-3.5" : ""}`}
            >
              {getFullTeamName(outcome.name)}
            </span>
            {heldOutcomes.includes(outcome.slug) && state !== "final" && (
              <span className={`text-sm ml-1 ${getOutcomePnL(outcome) >= 0 ? "text-[#22FF65]" : "text-[#FF2D30]"}`}>
                {formatPnL(getOutcomePnL(outcome))}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-0">
          {state !== "upcoming" && outcome.state !== "eliminated" && outcome.trend === "up" && (
            <span className={`text-[#22FF65] ${isFinal ? "mr-3" : "mr-2"} text-[62%]`}>▲</span>
          )}
          {state !== "upcoming" && outcome.state !== "eliminated" && outcome.trend === "down" && (
            <span className={`text-[#FF0000] ${isFinal ? "mr-2.5" : "mr-1.5"} text-[62%]`}>▼</span>
          )}
          {state !== "upcoming" && outcome.state !== "eliminated" && outcome.trend === "stable" && (
            <span className={`opacity-0 ${isFinal ? "mr-2" : "mr-0.5"}`}>▲</span>
          )}

          <OutcomePriceDisplay
            outcome={outcome}
            displayMode={displayMode}
            state={state}
            isFinal={isFinal}
            heldOutcomes={heldOutcomes}
          />

          <OutcomeTradeIndicators
            outcome={outcome}
            heldOutcomes={heldOutcomes}
            recentTrades={recentTrades}
            isFinal={isFinal}
            state={state}
          />
        </div>
      </div>
    </motion.div>
  )
}

export const BracketCardOutcomeRow = memo(BracketCardOutcomeRowComponent)
